import React, { useState, useCallback, useEffect } from 'react';
import { Alert, Platform } from 'react-native';
import { useRouter } from 'expo-router';
import NativePaywallScreen from '@/components/Paywall/NativePaywallScreen';
import { getSubscriptionPrice } from '@/constants/utils/subscriptionPricing';
import { useGabPurchase } from '@/context/GabPurchaseContext';
import { useAuthStore, useToastStore } from '@/store';
import { openNewChatComposer } from '@/lib/openHomeComposer';
import { hasValidPlusAccess } from '@/lib/purchaseUtils';
import {
  trackConversionEvent,
  setCheckoutSourceDimension,
  CONVERSION_ACTION,
} from '@/constants/utils/analyticsEvents';

export default function IosUpgradeScreen({ onClose, initialTier } = {}) {
  const router = useRouter();
  const currentUser = useAuthStore(state => state.user);
  const defaultTier = initialTier || (hasValidPlusAccess({
    isPlus: currentUser?.flags?.isPlus,
    isComped: currentUser?.flags?.isComped,
    plusExpiresAt: currentUser?.plusExpiresAt,
    purchase: currentUser?.activePurchase,
  }) ? 'premium' : 'plus');
  const [selectedTier, setSelectedTier] = useState(defaultTier);
  const [selectedPlan, setSelectedPlan] = useState(defaultTier === 'premium' ? 'monthly' : 'weekly');
  const [isLoading, setIsLoading] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [purchasePending, setPurchasePending] = useState(false);
  const { openCheckout, iap } = useGabPurchase();
  const fetchUser = useAuthStore(state => state.fetchUser);
  const { addToast } = useToastStore(state => state.actions);

  const useIap = Platform.OS === 'ios' && iap?.isConnected && iap?.products?.length > 0;
  const weeklyProduct = useIap ? iap.getProduct(iap.PRODUCT_IDS.WEEKLY) : null;
  const yearlyProduct = useIap ? iap.getProduct(iap.PRODUCT_IDS.YEARLY) : null;
  const premiumMonthlyProduct = useIap ? iap.getProduct(iap.PRODUCT_IDS.PREMIUM_MONTHLY) : null;
  const premiumYearlyProduct = useIap ? iap.getProduct(iap.PRODUCT_IDS.PREMIUM_YEARLY) : null;
  const pending = purchasePending || !!iap?.purchasePending;
  const selectedProduct = selectedTier === 'premium'
    ? selectedPlan === 'annual' ? premiumYearlyProduct : premiumMonthlyProduct
    : selectedPlan === 'annual' ? yearlyProduct : weeklyProduct;
  const dismiss = useCallback(() => {
    if (onClose) onClose();
    else openNewChatComposer(router, { work: false, replace: true });
  }, [onClose, router]);
  const selectTier = useCallback(tier => {
    setSelectedTier(tier);
    setSelectedPlan(plan => plan === 'annual' ? plan : tier === 'premium' ? 'monthly' : 'weekly');
  }, []);
  const iapNotReady = Platform.OS === 'ios' && (!iap?.isConnected || !iap?.products?.length);

  useEffect(() => {
    trackConversionEvent(CONVERSION_ACTION.PAYWALL_SHOWN, 'ios_paywall');
    setCheckoutSourceDimension('ios_paywall');
  }, []);

  const handlePurchase = useCallback(async () => {
    if (isLoading || isRestoring || pending || iap?.purchaseInProgress || !getSubscriptionPrice(selectedProduct, 'ios')) return;
    iap?.clearError?.();
    setIsLoading(true);
    try {
      const cadence = selectedPlan === 'annual' ? 'yearly' : selectedTier === 'premium' ? 'monthly' : 'weekly';
      const plusPlanId = selectedTier === 'premium' ? `premium-${cadence}` : cadence;
      console.log('[Paywall] Purchasing:', plusPlanId);
      const result = await openCheckout({ plusPlanId });
      if (result?.success && !result?.cancelled) {
        dismiss();
      } else if (result?.pending || result?.pendingValidation) {
        iap?.clearError?.();
        setPurchasePending(true);
      } else if (!result?.cancelled) {
        throw new Error(result?.error || 'Your purchase is still being verified. Use Restore; do not purchase again.');
      }
    } catch (error) {
      console.error('[Paywall] Purchase error:', error);
      if (!error?.message?.includes('cancel')) {
        Alert.alert(
          'Purchase Error',
          error?.message || 'Something went wrong. Please try again.',
          [
            { text: 'Retry', onPress: () => { iap?.fetchProducts?.(); } },
            { text: 'OK', style: 'cancel' },
          ]
        );
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedPlan, selectedTier, selectedProduct, openCheckout, iap, dismiss, isLoading, isRestoring, pending]);

  const handleSkip = () => {
    trackConversionEvent(CONVERSION_ACTION.PAYWALL_DISMISSED, 'ios_paywall');
    dismiss();
  };

  const handleRestore = useCallback(async () => {
    if (isRestoring || isLoading || iap?.purchaseInProgress) return;
    setIsRestoring(true);
    iap?.clearError?.();
    try {
      if (typeof iap?.restorePurchases === 'function') {
        const result = await iap.restorePurchases();
        if (result?.success) {
          setPurchasePending(false);
          addToast(result?.message || 'Subscription status refreshed.', 'success');
          try { await fetchUser?.(); }
          catch (refreshError) { console.warn('[Paywall] Restored purchase; account refresh will retry:', refreshError?.message); }
          const flags = useAuthStore.getState().user?.flags;
          if (selectedTier === 'premium' ? flags?.isPlusPremium : flags?.isPlus) {
            dismiss();
          }
        } else if (result?.pending) {
          setPurchasePending(true);
          addToast('Your purchase is still being verified. Please try Restore again shortly; do not purchase again.', 'info');
        } else {
          addToast(result?.error || 'Failed to restore.', 'error');
        }
      } else {
        addToast('The App Store is unavailable. Please try Restore again.', 'error');
      }
    } catch (error) {
      console.error('[Paywall] Restore failed:', error);
      addToast('Failed to restore. Please try again.', 'error');
    } finally {
      setIsRestoring(false);
    }
  }, [isRestoring, isLoading, iap, fetchUser, addToast, selectedTier, dismiss]);

  const handleRetry = async () => {
    try {
      if (!iap?.isConnected) await iap?.connect?.();
      await iap?.fetchProducts?.();
    } catch (error) {
      Alert.alert('Store unavailable', error?.message || 'Please try again shortly.');
    }
  };

  return (
    <NativePaywallScreen
      platform="ios"
      selectedTier={selectedTier}
      onSelectTier={selectTier}
      selectedPlan={selectedPlan}
      onSelectPlan={setSelectedPlan}
      weeklyPrice={getSubscriptionPrice(weeklyProduct, 'ios')}
      yearlyPrice={getSubscriptionPrice(yearlyProduct, 'ios')}
      premiumMonthlyPrice={getSubscriptionPrice(premiumMonthlyProduct, 'ios')}
      premiumYearlyPrice={getSubscriptionPrice(premiumYearlyProduct, 'ios')}
      onPurchase={handlePurchase}
      onRestore={handleRestore}
      onClose={handleSkip}
      onRetry={handleRetry}
      isPurchasing={isLoading || !!iap?.purchaseInProgress}
      isRestoring={isRestoring}
      purchasePending={pending}
      error={pending ? 'Your purchase is still being verified. Please try Restore again shortly; do not purchase again.' : iap?.error}
      storeStatus={iapNotReady ? (!iap?.isConnected ? 'Connecting to App Store…' : 'Loading plans…') : null}
    />
  );
}
